import { ArrowLeft, ArrowRight, Check, Images } from 'lucide-react'

import { Button } from '../../components/ui'
import type { SubmissionDraft } from '../../types/submission'
import { imageCount } from '../../types/submission'

interface ReviewStepProps {
  draft: SubmissionDraft
  onConsentChange: (accepted: boolean) => void
  onEdit: (step: 1 | 2) => void
  onSubmit: () => void
  submitting: boolean
}

export function ReviewStep({
  draft,
  onConsentChange,
  onEdit,
  onSubmit,
  submitting,
}: ReviewStepProps) {
  return (
    <section className="step-panel review-step" aria-labelledby="review-heading">
      <div className="step-panel__intro review-step__intro">
        <div>
          <h2 id="review-heading">Review your submission</h2>
          <p>Make sure each strain, image and credit is exactly where it belongs.</p>
        </div>
        <div className="review-step__totals">
          <strong>{draft.strains.length}</strong>
          <span>{draft.strains.length === 1 ? 'strain' : 'strains'}</span>
          <strong>{imageCount(draft)}</strong>
          <span>{imageCount(draft) === 1 ? 'image' : 'images'}</span>
        </div>
      </div>

      <section className="review-section">
        <header>
          <h3>Your details</h3>
          <button type="button" onClick={() => onEdit(1)}>Edit</button>
        </header>
        <dl className="review-details">
          <div><dt>Contact</dt><dd>{draft.details.contactName}</dd></div>
          <div><dt>Email</dt><dd>{draft.details.contactEmail}</dd></div>
          <div><dt>Credit as</dt><dd>{draft.details.creditName}</dd></div>
          <div>
            <dt>Social</dt>
            <dd>{draft.details.socialLinks.instagram || draft.details.socialLinks.website || 'Not provided'}</dd>
          </div>
        </dl>
      </section>

      <section className="review-section">
        <header>
          <h3>Strains & photos</h3>
          <button type="button" onClick={() => onEdit(2)}>Edit</button>
        </header>
        <div className="review-strains">
          {draft.strains.map((strain, index) => (
            <article className="review-strain" key={strain.id}>
              <div className="review-strain__heading">
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h4>{strain.name}</h4>
                  <p>{strain.growerOrBrand || 'No grower or brand provided'}</p>
                </div>
                <span className="review-strain__count">
                  <Images aria-hidden="true" /> {strain.assets.length}
                </span>
              </div>
              <div className="review-strain__images">
                {strain.assets.map((asset) => (
                  <figure key={asset.id}>
                    <img src={asset.previewUrl} alt={asset.name} />
                    <figcaption><Check aria-hidden="true" /> Ready</figcaption>
                  </figure>
                ))}
              </div>
              {strain.notes ? <p className="review-strain__notes">{strain.notes}</p> : null}
            </article>
          ))}
        </div>
      </section>

      <section className="release-section">
        <label className="release-check">
          <input
            type="checkbox"
            checked={draft.consentAccepted}
            onChange={(event) => onConsentChange(event.target.checked)}
          />
          <span className="release-check__box"><Check aria-hidden="true" /></span>
          <span>
            I have read and agree to the{' '}
            <a href="/contributor-terms" target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
              Contributor Terms
            </a>.
          </span>
        </label>
      </section>

      <footer className="step-actions review-actions">
        <Button variant="quiet" onClick={() => onEdit(2)} icon={<ArrowLeft size={20} />}>
          Back to photos
        </Button>
        <Button onClick={onSubmit} disabled={!draft.consentAccepted || submitting} icon={<ArrowRight size={22} />}>
          {submitting ? 'Submitting…' : 'Submit nug shots'}
        </Button>
      </footer>
    </section>
  )
}
